import React, { useMemo } from 'react';

import { zeosDialog } from '../components/Shared/confirm-dialog/ConfirmDialog';
import {
  EspmPropertyTypeSelect,
  EspmSubtypeSelect,
  MixedUseAllocationTable,
} from '../components/Shared/espm';
import { PlacesAutocomplete } from '../components/Shared/PlacesAutocomplete';
import { PropertyMatchingFactFields } from '../components/property/PropertyMatchingFactFields';
import { ServingUtilityFields } from '../components/property/ServingUtilityFields';
import { PropertyUtilityPanel } from '../components/property/PropertyUtilityPanel';
import { ZeosFileUpload } from '../components/Shared/ZeosFileUpload';
import { ZeosInput } from '../components/Shared/ZeosInput';
import { ZeosLabel } from '../components/Shared/ZeosLabel';
import { ZeosSimpleList } from '../components/Shared/ZeosSimpleList/ZeosSimpleList';
import { useEspmPropertyTypeTaxonomy } from '../hooks/useEspmPropertyTypeTaxonomy';
import { useUtilityProfile } from '../hooks/useUtilityProfile';
import { IPropertyFormDataType } from '../services/forms/FormDataTypes';
import { IListItemSimple } from '../services/zeos-api/contracts/ListUtility';
import { ZeosListEnum } from '../services/zeos-api/enums/ListUtility';

const LAND_OPTIONS = ["Owned", "Leased"] as const;
const WIFI_OPTIONS = ["Yes", "No"] as const;
const PAYS_BILL_OPTIONS = ["Owner", "Occupant", "Tenant-Resident", "Not Specified"] as const;
const PRIOR_WORK_NONE_ID = 2;

interface IProps {
  formData: IPropertyFormDataType;
  errors: Record<string, string>;
  handleChange: (
    field: keyof IPropertyFormDataType
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFormData: React.Dispatch<React.SetStateAction<IPropertyFormDataType>>;
  showCompanyDropdown?: boolean;
  companyList?: IListItemSimple[];
  hvacOptions?: IListItemSimple[];
  priorWorkTypeOptions?: { id: number; name: string }[];
  showServingUtilitySelectors?: boolean;
  /** IMPL-002 task F -- the owner-stated matching facts. */
  showMatchingFacts?: boolean;
  showSubmitButton?: boolean;
  onSubmit?: () => void;
  disabled?: boolean;
  showAddressLookup?: boolean;
}

export const PropertyForm = (props: IProps) => {
  const {
    formData,
    errors,
    handleChange,
    setFormData,
    showCompanyDropdown = false,
    companyList,
    hvacOptions,
    priorWorkTypeOptions,
    showServingUtilitySelectors = false,
    showMatchingFacts = false,
    showSubmitButton = false,
    onSubmit,
    disabled = false,
    showAddressLookup = true,
  } = props;

  const { categories: espmCategories, loading: espmLoading } =
    useEspmPropertyTypeTaxonomy();

  // One stored utility profile for the selectors and the panel, so a Re-check updates both.
  const savedPropertyId = Number(formData.propertyId ?? 0);
  const utilityProfile = useUtilityProfile(
    savedPropertyId,
    showServingUtilitySelectors && savedPropertyId > 0
  );

  const selectedEspmCategory = useMemo(
    () =>
      espmCategories.find(
        (c) =>
          c.espmPropertyTypeCategoryId === formData.espmPropertyTypeCategoryId
      ),
    [espmCategories, formData.espmPropertyTypeCategoryId]
  );

  const showEspmSubtype = Boolean(
    selectedEspmCategory &&
      !selectedEspmCategory.usesAllocation &&
      (selectedEspmCategory.details?.length ?? 0) > 0
  );

  const showMixedUseAllocation = Boolean(
    selectedEspmCategory?.usesAllocation
  );

  const handleEspmCategoryChange = (categoryId: number | undefined) => {
    const nextCategory = espmCategories.find(
      (c) => c.espmPropertyTypeCategoryId === categoryId
    );
    setFormData((prev) => ({
      ...prev,
      espmPropertyTypeCategoryId: categoryId,
      espmPropertyTypeDetailId: undefined,
      mixedUseComponents: nextCategory?.usesAllocation
        ? [{ detailId: 0, allocationPercent: undefined }]
        : undefined,
    }));
  };

  const handleAddressSelect = (parsedAddress: any) => {
    const willOverwrite = [
      formData.address,
      formData.city,
      formData.state,
      formData.zip,
    ].some((val) => val && val.length > 0);

    const newAddress = {
      address:
        `${parsedAddress.StreetAddress} ${parsedAddress.StreetName}`.trim(),
      city: parsedAddress.City,
      state: parsedAddress.State,
      zip: parsedAddress.PostalCode,
    };

    if (willOverwrite) {
      zeosDialog("This will overwrite your current address fields. Continue?", [
        {
          label: "Continue",
          onClick: () => {
            setFormData((prev) => ({ ...prev, ...newAddress }));
          },
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ]);
    } else {
      setFormData((prev) => ({ ...prev, ...newAddress }));
    }
  };

  return (
    <>
      {showCompanyDropdown && (
        <div className="col-span-12 px-2">
          <ZeosSimpleList
            listDefinition={companyList ?? []}
            onChange={(item) => {
              if (item) {
                setFormData((prev) => ({
                  ...prev,
                  companyId: Number(item.value),
                }));
              }
            }}
            placeholder="Select a company"
            searchable
            clearable
          />
          {errors.companyId && (
            <div className="text-orange-500 text-xs mt-1">
              {errors.companyId}
            </div>
          )}
        </div>
      )}
      <div className="col-span-12 px-2 relative">
        <ZeosInput
          name="propertyName"
          label="Property Name"
          type="text"
          placeholder="Enter Property Name"
          value={formData.propertyName || ""}
          onChange={handleChange("propertyName")}
          error={errors.propertyName}
          required={true}
          disabled={disabled}
        />
      </div>
      <div className="col-span-12 px-2 relative">
        <ZeosFileUpload
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          label="Property Image"
          disabled={disabled}
        />
      </div>
      {showAddressLookup && (
        <div className="col-span-12 px-2 space-y-1">
          <p className="text-sm text-gray-600">
            Start typing your address or manually enter it below.
          </p>
          <PlacesAutocomplete
            label="Address Lookup"
            placeholder="Search by Address or Business Name"
            onSelect={handleAddressSelect}
            disabled={disabled}
          />
        </div>
      )}
      <div className="col-span-6 px-2">
        <ZeosInput
          name="address"
          label="Address"
          type="text"
          value={formData.address || ""}
          onChange={handleChange("address")}
          error={errors.address}
          required={true}
          disabled={disabled}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="suite"
          label="Address (Suite / Room)"
          type="text"
          value={formData.suite || ""}
          onChange={handleChange("suite")}
          error={errors.suite}
          disabled={disabled}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="city"
          label="City"
          type="text"
          value={formData.city || ""}
          onChange={handleChange("city")}
          error={errors.city}
          required={true}
          disabled={disabled}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="state"
          label="State"
          type="text"
          value={formData.state || ""}
          onChange={handleChange("state")}
          error={errors.state}
          required={true}
          disabled={disabled}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="zip"
          label="Zipcode"
          type="text"
          value={formData.zip || ""}
          onChange={handleChange("zip")}
          error={errors.zip}
          required={true}
          disabled={disabled}
        />
      </div>
      {showServingUtilitySelectors ? (
        <ServingUtilityFields
          formData={formData}
          errors={errors}
          setFormData={setFormData}
          disabled={disabled}
          utilityProfile={utilityProfile}
        />
      ) : null}
      {showServingUtilitySelectors && savedPropertyId > 0 ? (
        // UTL P04: what ZEOS has on record for a saved property (3898).
        <PropertyUtilityPanel propertyId={savedPropertyId} utilityProfile={utilityProfile} />
      ) : null}
      <div className="col-span-3 px-2">
        <ZeosInput
          name="bin"
          label="BIN (7-digit)"
          type="text"
          value={formData.bin || ""}
          onChange={handleChange("bin")}
          error={errors.bin}
          disabled={disabled}
        />
      </div>
      <div className="col-span-3 px-2">
        <ZeosInput
          name="bbl"
          label="BBL (10-digit)"
          type="text"
          value={formData.bbl || ""}
          onChange={handleChange("bbl")}
          error={errors.bbl}
          disabled={disabled}
        />
      </div>
      <div className="col-span-12 grid grid-cols-12 gap-x-4">
        <div className="col-span-2 px-2">
          <ZeosLabel label="Land" />
          <div className="flex flex-col gap-2 mt-1">
            {LAND_OPTIONS.map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="radio"
                  name="land"
                  value={option}
                  checked={formData.land === option}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, land: option }))
                  }
                  className="mr-2"
                  disabled={disabled}
                />
                {option}
              </label>
            ))}
          </div>
          {errors.land && (
            <div className="text-orange-500 text-xs mt-1">{errors.land}</div>
          )}
        </div>
        <div className="col-span-2 px-2">
          <ZeosInput
            name="acreage"
            label="Land Acreage"
            type="text"
            value={formData.acreage || ""}
            onChange={handleChange("acreage")}
            size="sm"
            error={errors.acreage}
            disabled={disabled}
          />
        </div>
        <div className="col-span-2 px-2">
          <ZeosInput
            name="units"
            label="Number of Units"
            type="text"
            value={formData.units || ""}
            onChange={handleChange("units")}
            size="sm"
            error={errors.units}
            disabled={disabled}
          />
        </div>
        <div className="col-span-2 px-2">
          <ZeosInput
            name="floors"
            label="# of Floors"
            type="text"
            value={formData.floors || ""}
            onChange={handleChange("floors")}
            size="sm"
            error={errors.floors}
            disabled={disabled}
          />
        </div>
        <div className="col-span-4 ml-16">
          <ZeosLabel label="Wi-Fi in Units" />
          <div className="flex flex-col gap-2 mt-1">
            {WIFI_OPTIONS.map((option) => (
              <label key={option} className="flex items-center">
                <input
                  type="radio"
                  name="hasWifi"
                  value={option}
                  checked={formData.hasWifi === option}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, hasWifi: option }))
                  }
                  className="mr-2"
                  disabled={disabled}
                />
                {option}
              </label>
            ))}
          </div>
          {errors.hasWifi && (
            <div className="text-orange-500 text-xs mt-1">{errors.hasWifi}</div>
          )}
        </div>
      </div>
      <div className="col-span-6 px-2">
        <EspmPropertyTypeSelect
          categories={espmCategories}
          value={formData.espmPropertyTypeCategoryId}
          onChange={handleEspmCategoryChange}
          disabled={disabled}
          loading={espmLoading}
          error={errors.espmPropertyTypeCategoryId}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="sqft"
          label="Total Square Feet"
          type="text"
          value={formData.sqft || ""}
          onChange={handleChange("sqft")}
          error={errors.sqft}
          disabled={disabled}
        />
      </div>
      {showEspmSubtype && selectedEspmCategory && (
        <div className="col-span-12 px-2">
          <EspmSubtypeSelect
            details={selectedEspmCategory.details}
            value={formData.espmPropertyTypeDetailId}
            requiresSubtype={selectedEspmCategory.requiresSubtype}
            disabled={disabled}
            error={errors.espmPropertyTypeDetailId}
            onChange={(detailId) => {
              setFormData((prev) => ({
                ...prev,
                espmPropertyTypeDetailId: detailId,
              }));
            }}
          />
        </div>
      )}
      {showMixedUseAllocation && selectedEspmCategory && (
        <div className="col-span-12 px-2">
          <MixedUseAllocationTable
            componentOptions={selectedEspmCategory.details}
            value={formData.mixedUseComponents ?? []}
            disabled={disabled}
            error={errors.mixedUseComponents}
            onChange={(rows) => {
              setFormData((prev) => ({
                ...prev,
                mixedUseComponents: rows,
                espmPropertyTypeDetailId: undefined,
              }));
            }}
          />
        </div>
      )}
      <div className="col-span-6 px-2">
        <ZeosInput
          name="yearBuilt"
          label="Age of Building (Year Built)"
          type="number"
          value={formData.yearBuilt || ""}
          onChange={handleChange("yearBuilt")}
          error={errors.yearBuilt}
          required={true}
          disabled={disabled}
        />
      </div>
      <div className="col-span-6 px-2">
        <ZeosInput
          name="roofSize"
          label="Roof Size (sq. feet)"
          type="text"
          value={formData.roofSize || ""}
          onChange={handleChange("roofSize")}
          error={errors.roofSize}
          disabled={disabled}
        />
      </div>
      {hvacOptions && hvacOptions.length > 0 && (
        <div className="col-span-12 px-2">
          <ZeosLabel label="HVAC system type" />
          <p className="text-xs text-gray-500 mb-2">
            Select all that apply. Many buildings use more than one system.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {hvacOptions.map((option) => {
              const selected =
                formData.hvacPrimaryTypeIds?.includes(option.dataKey) ?? false;
              return (
                <label key={option.dataKey} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      setFormData((prev) => {
                        const next = new Set(prev.hvacPrimaryTypeIds ?? []);
                        if (next.has(option.dataKey)) {
                          next.delete(option.dataKey);
                        } else {
                          next.add(option.dataKey);
                        }
                        return {
                          ...prev,
                          hvacPrimaryTypeIds: Array.from(next),
                        };
                      });
                    }}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span>{option.display}</span>
                </label>
              );
            })}
          </div>
          {errors.hvacPrimaryTypeIds && (
            <div className="text-orange-500 text-xs mt-1">
              {errors.hvacPrimaryTypeIds}
            </div>
          )}
        </div>
      )}
      <div className="col-span-6 px-2">
        <ZeosLabel label="Who Pays the Energy Bill?" />
        <div className="flex flex-col gap-2 mt-1">
          {PAYS_BILL_OPTIONS.map((option) => {
            // "Not Specified" clears the field rather than being a 4th
            // stored value — it's selected whenever paysBill is empty/unset.
            const isNotSpecified = option === "Not Specified";
            const selected = isNotSpecified
              ? !formData.paysBill
              : formData.paysBill === option;
            return (
              <label key={option} className="flex items-center">
                <input
                  type="radio"
                  name="paysBill"
                  value={option}
                  checked={selected}
                  onChange={() =>
                    setFormData((prev) => ({
                      ...prev,
                      paysBill: isNotSpecified ? "" : option,
                    }))
                  }
                  className="mr-2"
                  disabled={disabled}
                />
                {option}
              </label>
            );
          })}
        </div>
        {errors.paysBill && (
          <div className="text-orange-500 text-xs mt-1">
            {errors.paysBill}
          </div>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Helps us determine who benefits most from energy savings
          recommendations.
        </p>
      </div>
      {priorWorkTypeOptions && priorWorkTypeOptions.length > 0 && (
        <div className="col-span-12 px-2">
          <ZeosLabel label="Prior retrofit and building upgrade work *" />
          <p className="text-xs text-gray-500 mt-1 mb-2">
            Select all that apply. Choose "None" if no prior retrofit or upgrade work has been done.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {priorWorkTypeOptions.map((option) => {
              const selected = formData.priorWorkTypeIds?.includes(option.id) ?? false;
              return (
                <label key={option.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {
                      setFormData((prev) => {
                        const current = new Set(prev.priorWorkTypeIds ?? []);
                        if (current.has(option.id)) {
                          current.delete(option.id);
                        } else {
                          if (option.id === PRIOR_WORK_NONE_ID) {
                            // "None" selected — clear all other selections
                            current.clear();
                          } else {
                            // Work type selected — remove "None" if present
                            current.delete(PRIOR_WORK_NONE_ID);
                          }
                          current.add(option.id);
                        }
                        return {
                          ...prev,
                          priorWorkTypeIds: Array.from(current),
                        };
                      });
                    }}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span>{option.name}</span>
                </label>
              );
            })}
          </div>
          {errors.priorWorkTypeIds && (
            <div className="text-orange-500 text-xs mt-1">
              {errors.priorWorkTypeIds}
            </div>
          )}
        </div>
      )}
      {showMatchingFacts ? (
        <PropertyMatchingFactFields
          formData={formData}
          setFormData={setFormData}
          disabled={disabled}
        />
      ) : null}
      {showSubmitButton && (
        <div className="flex justify-end mt-4 space-x-3">
          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
          >
            Save Property
          </button>
        </div>
      )}
    </>
  );
};
